import { useMemo, useState, type ReactNode } from "react";
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import ContentLayout from "../components/UI/Layout/ContentLayout.tsx";
import { Tabs } from "../components/UI/Tabs/Tabs";
import PageLayout from "../components/UI/Layout/PageLayout";
import LoyersShow from "../components/Dashboard/LoyersShow";
import MassSynthese from "../components/Tenant/MassSynthese";
import {
  PaymentType,
  TenantPaymentStatus,
  TenantStatus,
  // @ts-expect-error pure js
} from "../models/tenant_old";
// @ts-expect-error pure js
import useTenants from "../hooks/use-tenants";
import MassAutoActions from "../components/Tenant/MassAutoActions";
import AplManagementTab from "../components/Tenant/Apl/AplManagementTab";
import DiagnosticsSynthese from "../components/Property/DiagnosticsSynthese";
// @ts-expect-error pure js
import useProperties from "../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../hooks/use-property-folders";
import {
  buildPropertyFoldersTree,
  findPropertyFolder,
  getIncludedPropertyAndFolderIds,
  // @ts-expect-error pure js
} from "../utils/properties";
import {
  BalanceDomain,
  DOMAINE_ID_SEPARATOR,
} from "../utils/constants/balances";
// @ts-expect-error pure js
import { useAllTenantsBalances } from "../hooks/use-balances";
// @ts-expect-error pure js
import Prelevement from "../components/Tenant/Prelevement";
import HeaderLayout from "../components/UI/Layout/HeaderLayout.tsx";
import FilterBar from "../components/UI/FilterBar/FilterBar.tsx";
import FilterSection from "../components/UI/FilterBar/FilterSection.tsx";
import { Selectable } from "../components/UI/Selectable";
// @ts-expect-error pure js
import { useConfirm } from "../hooks/use-confirm.jsx";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../components/UI/SelectStandalone";
import Text from "../components/UI/Text";
import { IconRefresh } from "@tabler/icons-react";
import BulkAvisEcheanceModal from "../components/Tenant/BulkAvisEcheanceModal";
import BulkRevisionLoyerModal from "../components/Tenant/BulkRevisionLoyerModal";
import BulkQuittanceModal from "../components/Tenant/Bulk/BulkQuittanceModal";
import BulkCourriersModal from "../components/Tenant/BulkCourriersModal";
import type { Tenant } from "../models/tenant";
import type { Property } from "../models/property";
import type { PropertyFolder } from "../models/propertyFolder";

const STATUS_OPTIONS = [
  { value: TenantPaymentStatus.A_JOUR, label: "À jour" },
  { value: TenantPaymentStatus.RETARD, label: "En retard" },
  { value: TenantPaymentStatus.IMPAYE, label: "En impayé" },
  { value: TenantPaymentStatus.DG_IMPAYE, label: "Dépôt de garantie impayé" },
];

interface BienOption {
  value: string;
  label: string;
}

interface BienOptionGroup {
  label: string;
  options: BienOption[];
}

function extractPropertyIdFilters(
  propertyFolders: PropertyFolder[],
  properties: Property[],
  filterBien: string[],
): Set<number> {
  const propertiesAndFolders = buildPropertyFoldersTree(
    propertyFolders,
    properties,
  );

  const propertyIds = new Set<number>();
  filterBien.forEach((filter) => {
    if (filter.startsWith(BalanceDomain.FOLDER)) {
      const folderId = parseInt(filter.split(DOMAINE_ID_SEPARATOR)[1]);
      const folder = findPropertyFolder(
        propertiesAndFolders.property_folders,
        folderId,
      );
      if (folder) {
        folder.properties.forEach((property: Property) =>
          propertyIds.add(property.id),
        );
        const { propertyIds: includedPropertyIds } =
          getIncludedPropertyAndFolderIds(folder);
        includedPropertyIds.forEach((propertyId: number) =>
          propertyIds.add(propertyId),
        );
      }
    } else if (filter.startsWith(BalanceDomain.PROPERTY)) {
      propertyIds.add(parseInt(filter.split(DOMAINE_ID_SEPARATOR)[1]));
    }
  });

  return propertyIds;
}

interface ConfirmPayload {
  tenantIds: number[];
  resetSelected: () => void;
}

export default function PilotagePage() {
  const { properties } = useProperties() as { properties: Property[] };
  const { propertyFolders } = usePropertyFolders() as {
    propertyFolders: PropertyFolder[];
  };
  const { tenants, upToDateAllTenants } = useTenants({
    status: TenantStatus.Actif.status,
  }) as {
    tenants: Tenant[];
    upToDateAllTenants: (tenantIds: number[]) => Promise<void>;
  };
  const [search, setSearch] = useState("");
  const [filterBien, setFilterBien] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useQueryState(
    "statut",
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const { groupedBalances, mutate: refreshBalances } = useAllTenantsBalances(
    false,
    { groupBy: { attr: "id_tenant", unique: true } },
  ) as {
    groupedBalances: Record<number, unknown>;
    mutate: () => Promise<void>;
  };
  const [Confirm, openConfirm] = useConfirm() as [
    (props: {
      onAnswer: (ok: boolean, payload: ConfirmPayload) => void;
      size?: string;
      children?: ReactNode;
    }) => ReactNode,
    (payload: ConfirmPayload) => void,
  ];

  const [bulkAvisPayload, setBulkAvisPayload] = useState<{
    tenantIds: number[];
    resetSelected: () => void;
  } | null>(null);

  const [bulkRevisionPayload, setBulkRevisionPayload] = useState<{
    tenantIds: number[];
    resetSelected: () => void;
  } | null>(null);

  const [bulkQuittancePayload, setBulkQuittancePayload] = useState<{
    tenantIds: number[];
    resetSelected: () => void;
  } | null>(null);

  const [bulkCourriersPayload, setBulkCourriersPayload] = useState<{
    tenantIds: number[];
    resetSelected: () => void;
  } | null>(null);

  const bienOptionGroups: BienOptionGroup[] = [
    {
      label: "Dossiers",
      options: propertyFolders.map((folder) => ({
        value: `${BalanceDomain.FOLDER}${DOMAINE_ID_SEPARATOR}${folder.id}`,
        label: folder.nom,
      })),
    },
    {
      label: "Biens",
      options: properties.map((property) => ({
        value: `${BalanceDomain.PROPERTY}${DOMAINE_ID_SEPARATOR}${property.id}`,
        label: property.nom,
      })),
    },
  ];

  const flatBienOptions = bienOptionGroups.flatMap((g) => g.options);
  const selectedBienOptions = filterBien
    .map((v) => flatBienOptions.find((o) => o.value === v))
    .filter((opt): opt is BienOption => Boolean(opt));

  const filteredTenants = useMemo(() => {
    const searchLower = search.toLowerCase();
    return tenants.filter((tenant) => {
      const balanceTenant = groupedBalances[tenant.id];

      const [loyerStatus, dgStatus] = TenantPaymentStatus.get(
        balanceTenant,
        (tenant.loyer_hc ?? 0) + (tenant.charges ?? 0),
      );

      if (searchLower && !tenant.nom?.toLowerCase().includes(searchLower)) {
        return false;
      }

      if (
        filterBien.length > 0 &&
        !extractPropertyIdFilters(propertyFolders, properties, filterBien).has(
          tenant.id_bien,
        )
      ) {
        return false;
      }

      return (
        filterStatus.length == 0 ||
        filterStatus.includes(loyerStatus) ||
        filterStatus.includes(dgStatus)
      );
    });
  }, [
    tenants,
    groupedBalances,
    search,
    filterBien,
    filterStatus,
    properties,
    propertyFolders,
  ]);

  const activeFiltersCount =
    (filterStatus.length > 0 ? 1 : 0) + (filterBien.length > 0 ? 1 : 0);

  function clearFilters() {
    setFilterStatus(null);
    setFilterBien([]);
  }

  const filterBarProps = {
    searchValue: search,
    onSearchChange: setSearch,
    searchPlaceholder: "Rechercher un locataire",
    activeFiltersCount,
    onClearFilters: clearFilters,
  };

  const bienFilterSelect = flatBienOptions.length > 0 && (
    <ReactSelectStandalone
      label="Biens et dossiers"
      isMulti
      options={bienOptionGroups}
      value={selectedBienOptions}
      onChange={(opts: BienOption[] | null) =>
        setFilterBien((opts || []).map((o) => o.value))
      }
      placeholder="Tous les biens"
      menuPortalTarget={document.body}
    />
  );

  const filterSections = (
    <>
      <FilterSection
        title="Statut"
        options={STATUS_OPTIONS}
        values={filterStatus}
        onChange={setFilterStatus}
        defaultOpen
      />
      {bienFilterSelect}
    </>
  );

  // Les onglets Suivi documents et Diagnostics ont leurs propres recherche et
  // statuts : seul le filtre « Biens et dossiers » leur est commun.
  const bienFilteredTenants = useMemo(() => {
    if (filterBien.length === 0) {
      return tenants;
    }
    const propertyIds = extractPropertyIdFilters(
      propertyFolders,
      properties,
      filterBien,
    );
    return tenants.filter((tenant) => propertyIds.has(tenant.id_bien));
  }, [tenants, propertyFolders, properties, filterBien]);

  const filteredProperties = useMemo(() => {
    if (filterBien.length === 0) {
      return properties;
    }
    const propertyIds = extractPropertyIdFilters(
      propertyFolders,
      properties,
      filterBien,
    );
    return properties.filter((property) => propertyIds.has(property.id));
  }, [properties, propertyFolders, filterBien]);

  return (
    <PageLayout isBailleur>
      <Confirm
        onAnswer={async (ok, { tenantIds, resetSelected }) => {
          if (ok && tenantIds.length > 0) {
            try {
              await upToDateAllTenants(tenantIds);
              resetSelected();
              await refreshBalances();
            } catch (error) {
              console.error(error);
            }
          }
        }}
        size={"small"}
      >
        <Text>
          Cette fonction ajoute un paiement pour le dernier mois d'appel de
          chaque locataire sélectionné, s'il présente un moins-perçu. Les mois
          précédents ne sont pas modifiés : pour un impayé plus ancien ou un
          trop-perçu, vous devez éditer manuellement la ligne correspondante.
        </Text>
      </Confirm>
      <BulkAvisEcheanceModal
        open={bulkAvisPayload !== null}
        onOpenChange={(o) => {
          if (!o) setBulkAvisPayload(null);
        }}
        selectedTenantIds={bulkAvisPayload?.tenantIds ?? []}
        resetSelection={bulkAvisPayload?.resetSelected}
      />
      <BulkRevisionLoyerModal
        open={bulkRevisionPayload !== null}
        onOpenChange={(o) => {
          if (!o) setBulkRevisionPayload(null);
        }}
        selectedTenantIds={bulkRevisionPayload?.tenantIds ?? []}
        resetSelection={bulkRevisionPayload?.resetSelected}
      />
      <BulkQuittanceModal
        open={bulkQuittancePayload !== null}
        onOpenChange={(o) => {
          if (!o) setBulkQuittancePayload(null);
        }}
        selectedTenantIds={bulkQuittancePayload?.tenantIds ?? []}
        resetSelection={bulkQuittancePayload?.resetSelected}
      />
      <BulkCourriersModal
        open={bulkCourriersPayload !== null}
        onOpenChange={(o) => {
          if (!o) setBulkCourriersPayload(null);
        }}
        selectedTenantIds={bulkCourriersPayload?.tenantIds ?? []}
        resetSelection={bulkCourriersPayload?.resetSelected}
      />
      <ContentLayout
        header={
          <HeaderLayout
            title={"Pilotage"}
            breadcrumb={[
              { label: "Tableau de bord", to: "/bailleur" },
              { label: "Pilotage" },
            ]}
          />
        }
        body={
          <Tabs id="pilotage" defaultValue="comptable">
            <div className="container bg-blue paddingT10 border-blue">
              <div className="content">
                <Tabs.List>
                  <Tabs.Tab value="comptable">Suivi comptable</Tabs.Tab>
                  <Tabs.Tab value="documents">Suivi documents</Tabs.Tab>
                  <Tabs.Tab value="diagnostics">Diagnostics</Tabs.Tab>
                  <Tabs.Tab value="auto-actions">Automatisations</Tabs.Tab>
                  <Tabs.Tab value="prelevements">Prélèvements</Tabs.Tab>
                  <Tabs.Tab value="apl">Gestion APL</Tabs.Tab>
                </Tabs.List>
              </div>
            </div>
            <div className="blueSpace"></div>
            <div className="content">
              <Tabs.Panel value="comptable">
                <Selectable items={filteredTenants}>
                  <FilterBar
                    {...filterBarProps}
                    leftActions={
                      <>
                        <Selectable.ActionButton
                          icon={IconRefresh}
                          onClick={(
                            selectedItems: (number | string)[],
                            resetSelected: () => void,
                          ) =>
                            openConfirm({
                              tenantIds: selectedItems as number[],
                              resetSelected,
                            })
                          }
                        >
                          Mettre à jour les loyers
                        </Selectable.ActionButton>
                        <Selectable.ActionButton
                          variant="secondary"
                          onClick={(
                            selectedItems: (number | string)[],
                            resetSelected: () => void,
                          ) =>
                            setBulkAvisPayload({
                              tenantIds: selectedItems as number[],
                              resetSelected,
                            })
                          }
                        >
                          Envoyer des avis d'échéance
                        </Selectable.ActionButton>
                        <Selectable.ActionButton
                          variant="secondary"
                          onClick={(
                            selectedItems: (number | string)[],
                            resetSelected: () => void,
                          ) =>
                            setBulkRevisionPayload({
                              tenantIds: selectedItems as number[],
                              resetSelected,
                            })
                          }
                        >
                          Réviser les loyers
                        </Selectable.ActionButton>
                        <Selectable.ActionButton
                          variant="secondary"
                          onClick={(
                            selectedItems: (number | string)[],
                            resetSelected: () => void,
                          ) =>
                            setBulkQuittancePayload({
                              tenantIds: selectedItems as number[],
                              resetSelected,
                            })
                          }
                        >
                          Envoyer des quittances
                        </Selectable.ActionButton>
                        <Selectable.ActionButton
                          variant="secondary"
                          onClick={(
                            selectedItems: (number | string)[],
                            resetSelected: () => void,
                          ) =>
                            setBulkCourriersPayload({
                              tenantIds: selectedItems as number[],
                              resetSelected,
                            })
                          }
                        >
                          Envoyer des courriers
                        </Selectable.ActionButton>
                      </>
                    }
                  >
                    {filterSections}
                  </FilterBar>
                  <LoyersShow tenants={filteredTenants} />
                </Selectable>
              </Tabs.Panel>
              <Tabs.Panel value="documents">
                <MassSynthese
                  tenants={bienFilteredTenants}
                  bienFilter={bienFilterSelect}
                  bienFilterCount={filterBien.length > 0 ? 1 : 0}
                  onClearBienFilter={() => setFilterBien([])}
                />
              </Tabs.Panel>
              <Tabs.Panel value="diagnostics">
                <DiagnosticsSynthese
                  properties={filteredProperties}
                  propertyFolders={propertyFolders}
                  bienFilter={bienFilterSelect}
                  bienFilterCount={filterBien.length > 0 ? 1 : 0}
                  onClearBienFilter={() => setFilterBien([])}
                />
              </Tabs.Panel>
              <Tabs.Panel value="auto-actions">
                <FilterBar {...filterBarProps}>{filterSections}</FilterBar>
                <MassAutoActions tenants={filteredTenants} />
              </Tabs.Panel>
              <Tabs.Panel value="prelevements">
                <FilterBar {...filterBarProps}>{filterSections}</FilterBar>
                <Prelevement
                  tenants={filteredTenants.filter(
                    (t) => t.type_paiement === PaymentType.PRELEVEMENT,
                  )}
                />
              </Tabs.Panel>
              <Tabs.Panel value="apl">
                <FilterBar {...filterBarProps}>{filterSections}</FilterBar>
                <AplManagementTab tenants={filteredTenants} />
              </Tabs.Panel>
            </div>
          </Tabs>
        }
      />
    </PageLayout>
  );
}
